<script setup lang="ts">
import { LucideChartColumnIncreasing, LucideCircleX, LucideSearch } from 'lucide-vue-next'
import MainMenu from './MainMenu.vue'
import MobileSidebar from './MobileSidebar.vue'
import { MAIN_MENU } from './consts'
import { WidgetStickyRates } from '~/components/widget/sticky-rates'
import MegaMenuNavItems from '~/components/widget/layout-header/MegaMenuNavItems.vue'
import MegaMenu from '~/components/widget/layout-header/MegaMenu.vue'
import MegaMenuAccentedLink from '~/components/widget/layout-header/MegaMenuAccentedLink.vue'
import MegaMenuRegularLink from '~/components/widget/layout-header/MegaMenuRegularLink.vue'
import { WidgetReviveAd } from '~/components/widget/revive-ad'
import { WidgetCompareDropdown } from '~/components/widget/compare-dropdown'
import UserAuthWidget from '~/components/widget/layout-header/UserAuthWidget.vue'
import LanguageSwitcher from '~/components/widget/layout-header/LanguageSwitcher.vue'

const WidgetLayoutHeaderSearch = defineAsyncComponent(
  () => import('~/components/widget/layout-header/search/WidgetLayoutHeaderSearch.vue')
)

const route = useRoute()

// Check route name instead of path to support i18n
// Route name can be 'index', 'index___ky', 'index___ru', etc.
const isHomePage = computed(() => route?.name?.toString().startsWith('index'))
const spaceForStickyRates = computed(() => !isHomePage.value)
const mainSearch = ref<boolean>(false)

watch(
  () => route?.name,
  () => {
    mainSearch.value = false
  }
)

// Handle escape key to close search - wrapped in effect to avoid tree-shaking issues in production build
watchEffect((onInvalidate) => {
  if (!import.meta.client) return

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      mainSearch.value = false
    }
  }

  const win = globalThis.window
  if (win) {
    win.addEventListener('keydown', handleKeyDown)
    onInvalidate(() => {
      win.removeEventListener('keydown', handleKeyDown)
    })
  }
})
</script>

<template>
  <div
    :class="
      spaceForStickyRates
        ? 'min-h-[98px] md:min-h-[116px] 3xl:min-h-[7.438rem]'
        : 'min-h-[68px] md:min-h-[79px] 3xl:min-h-[4.938rem]'
    "
  />
  <header id="layout-header" class="fixed z-10 w-full border-b bg-white py-4 md:py-5">
    <!-- Fixed row height (matches the logo) keeps the header from resizing when its items load -->
    <Container class="flex h-9 items-center md:h-[38px] 3xl:h-[2.375rem]">
      <div class="flex items-center gap-2 sm:gap-3">
        <MobileSidebar :menu-links="MAIN_MENU" />
        <NuxtLinkLocale to="/" :aria-label="$t('common.link_to_homepage_bankskg')">
          <Logo class="h-[28px] w-auto md:h-[38px] 3xl:h-[2.375rem]" />
        </NuxtLinkLocale>
      </div>
      <MainMenu :menu-links="MAIN_MENU" />
      <div class="ml-auto flex gap-2 pl-2 sm:gap-3 sm:pl-4">
        <!-- Both variants are rendered and switched by CSS so SSR HTML is identical for every device -->
        <div class="hidden lg:flex">
          <MegaMenu :trigger-name="$t('common.exchange_rates')" align="end">
            <template #icon><LucideChartColumnIncreasing class="size-5" /></template>
            <template #header>
              <MegaMenuAccentedLink to="/rates">{{
                $t('menu.rates_currencies.title')
              }}</MegaMenuAccentedLink>
            </template>
            <MegaMenuNavItems v-for="(group, groupIndex) in MAIN_MENU.rates" :key="groupIndex">
              <MegaMenuRegularLink
                v-for="(link, linkIndex) in group"
                :key="`${groupIndex}-${linkIndex}`"
                :to="link.url"
              >
                {{ $t(link.title) }}
              </MegaMenuRegularLink>
            </MegaMenuNavItems>
            <template #footer>
              <div class="border-t pt-4">
                <MegaMenuAccentedLink to="/rates/nbkr">{{
                  $t('menu.rates_currencies.nbkr')
                }}</MegaMenuAccentedLink>
              </div>
            </template>
            <template #banner>
              <div class="w-full">
                <WidgetReviveAd zoneid="36" />
              </div>
            </template>
          </MegaMenu>
        </div>
        <Button
          class="hidden min-[400px]:inline-flex lg:hidden"
          variant="gray"
          size="icon"
          :title="$t('common.exchange_rates')"
          :aria-label="$t('common.exchange_rates')"
          as-child
        >
          <NuxtLinkLocale to="/rates">
            <LucideChartColumnIncreasing class="size-5" />
          </NuxtLinkLocale>
        </Button>
        <WidgetCompareDropdown />
        <Button
          variant="gray"
          size="icon"
          :title="$t('menu.actions.search')"
          :aria-label="$t('menu.actions.search')"
          @click="mainSearch = !mainSearch"
        >
          <LucideSearch class="size-5" />
        </Button>
        <LanguageSwitcher />
        <ClientOnly>
          <template #fallback>
            <div class="h-9 w-9 animate-pulse rounded-lg bg-gray-200" />
          </template>
          <UserAuthWidget />
        </ClientOnly>
      </div>
      <div
        v-if="mainSearch"
        class="absolute left-0 top-0 z-50 flex w-full justify-center bg-white pb-3 pt-4"
      >
        <Container class="flex w-full gap-3 px-4 align-top sm:align-middle">
          <WidgetLayoutHeaderSearch @hide="mainSearch = false" />
          <Button
            size="icon"
            variant="link"
            class="mt-3.5 size-6 text-red-400 hover:text-red-400"
            @click="mainSearch = false"
          >
            <LucideCircleX />
          </Button>
        </Container>
      </div>
    </Container>
    <WidgetStickyRates />
  </header>
</template>
